
import {FC, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useParams, useLocation} from "react-router-dom";
import {useFlags} from "launchdarkly-react-client-sdk";
import {
  ClientEntity,
  clientBranchIsComplex,
  clientBranchIsFullyOperational
} from "@common-core/coat-operational-hierarchy-appsync-model";
import {BackendError} from "@common-core/runtime-js/backend";
import {Button} from "@interstate/components/Button";
import {ArrowUturnLeftIcon} from "@interstate/components/Icons";
import {Switch} from "@interstate/components/Switch";
import {ErrorHandler, Interceptor} from "../../../utils";
import {AlertableError} from "../../../alerting";
import {ActivityIndicator} from "../../../activity-indicator";
import {useGraphqlLazyQuery} from "../../../amplify/hooks";
import {lookupClientEntityWithHierarchy as lookupClientEntityWithHierarchyQuery} from "../../../amplify/graphql";
import {TriggerResult} from "../../../amplify/hooks/types";
import {
  IdentifiedRouteParams,
  useNavigation,
  useSidebarItems
} from "../../../navigation";
import {ErrorPage, PageHeading, PageTemplate} from "../../../layout";
import {features} from "../../../features";
import {EntitySourceType} from "../../EntitySourceType";
import {
  LookupClientEntityWithHierarchyData,
  LookupClientEntityWithHierarchyVariables
} from "../../backend";
import {CreatePlanNode} from "./view-model";
import {
  prepareSimplePlan,
  toggleAllSelection,
  toggleNodeSelection
} from "./helpers";
import {toCreatePlanNode} from "./transforms";
import {PlanProvider} from "./context";
import {HierarchyBuilder} from "./HierarchyBuilder";
import "./CreateHierarchyPage.scss";

export const CreateHierarchyPage: FC = () => {
  const {t} = useTranslation();
  const flags = useFlags();
  const {id} = useParams<IdentifiedRouteParams>();
  const {search} = useLocation();
  const [simulateError, setSimulateError] = useState<boolean>(false);
  const [error, setError] = useState<any>();
  const [from, setFrom] = useState<ClientEntity | undefined>();
  const [hierarchy, setHierarchy] = useState<ClientEntity | undefined>();
  const [plan, setPlan] = useState<CreatePlanNode | undefined>();
  const {navigateToEntityFromEvent} = useNavigation();
  const sidebarItems = useSidebarItems();

  const [lookupClientWithHierarchy] = useGraphqlLazyQuery<
    LookupClientEntityWithHierarchyData,
    LookupClientEntityWithHierarchyVariables
  >({
    amplifyQuery: lookupClientEntityWithHierarchyQuery
  });

  const assertFound = (
    result: TriggerResult<LookupClientEntityWithHierarchyData>
  ): ClientEntity => {
    if (!result.data?.entity) {
      throw new BackendError(
        t("create-operational-hierarchy.errors.not-found", {
          id
        }),
        404
      );
    }
    return result.data.entity;
  };

  const captureFrom: Interceptor<ClientEntity> = (
    from: ClientEntity
  ): ClientEntity => {
    setFrom(from);
    return from;
  };

  const captureHierarchy: Interceptor<ClientEntity> = (
    from: ClientEntity
  ): ClientEntity => {
    if (!from.hierarchy) {
      throw new AlertableError(
        t("create-operational-hierarchy.errors.no-hierarchy", {
          id: from.caId
        }),
        "error"
      );
    }
    if (
      !search.includes("bypass-complex=true") &&
      clientBranchIsComplex(from.hierarchy, from)
    ) {
      throw new AlertableError(
        t("create-operational-hierarchy.errors.not-simple", {
          id: from.caId
        }),
        "error"
      );
    }
    if (
      !search.includes("bypass-operational=true") &&
      clientBranchIsFullyOperational(from.hierarchy, from)
    ) {
      throw new AlertableError(
        t("create-operational-hierarchy.errors.fully-operational", {
          id: from.caId
        }),
        "info"
      );
    }
    setHierarchy(from.hierarchy);
    return from.hierarchy;
  };

  const onError: ErrorHandler = (error: any) => {
    console.error(error);
    setError(error);
  };

  useEffect(() => {
    setError(undefined);
    lookupClientWithHierarchy({variables: {id}})
      .then(assertFound)
      .then(captureFrom)
      .then(captureHierarchy)
      .then(toCreatePlanNode)
      .then(prepareSimplePlan(id))
      .then(setPlan)
      .catch(onError);
  }, [id]);

  // Return the ErrorPage when loading is complete and the entity was not found
  if (error) {
    return (
      <ErrorPage
        id={"create-operational-hierarchy-page"}
        severity={error?.severity || "error"}
        error={error.message}
        logo={false}
        banner={
          <PageHeading
            title={t("create-operational-hierarchy.page-title")}
            summary={t("create-operational-hierarchy.page-summary")}
          />
        }
        sidebarItems={sidebarItems}>
        <div className={"error-actions"}>
          {error?.status !== 404 && (
            <Button
              id={"back-to-client-button"}
              data-testid={"back-to-client-button"}
              className={"back-to-client-button"}
              buttonStyle={"tertiary"}
              startIcon={<ArrowUturnLeftIcon />}
              onClick={event => {
                navigateToEntityFromEvent(
                  event,
                  id,
                  EntitySourceType.CLIENT_HIERARCHY
                );
              }}>
              {t("create-operational-hierarchy.back-to-client-entity")}
            </Button>
          )}
        </div>
      </ErrorPage>
    );
  }

  // Return an empty page with the ActivityIndicator while loading the entity
  if (!from || !hierarchy || !plan) {
    return (
      <PageTemplate
        id={"create-operational-hierarchy-page"}
        banner={
          <PageHeading
            title={t("create-operational-hierarchy.page-title")}
            summary={t("create-operational-hierarchy.page-summary")}
          />
        }
        sidebarItems={sidebarItems}>
        <ActivityIndicator />
      </PageTemplate>
    );
  }

  return (
    <PageTemplate
      id={"create-operational-hierarchy-page"}
      banner={
        <PageHeading
          title={t("create-operational-hierarchy.page-title")}
          summary={t("create-operational-hierarchy.page-summary")}
        />
      }
      sidebarItems={sidebarItems}>
      <hgroup className={"entity-header"} data-testid={"entity-header"}>
        <h2 className={"entity-business-name"}>{from.custName}</h2>
        {flags[features.dev.createSimpleOperationalHierarchy.enableDemo] && (
          <div>
            <Switch
              labelSize={"md"}
              label={"Simulate Backend Error"}
              onClick={event =>
                setSimulateError(event.target.value === "checked")
              }
            />
          </div>
        )}
      </hgroup>
      <PlanProvider
        from={from}
        hierarchy={hierarchy}
        initial={plan}
        toggleAll={toggleAllSelection}
        toggleOne={toggleNodeSelection}>
        <HierarchyBuilder simulateError={simulateError} />
      </PlanProvider>
    </PageTemplate>
  );
};
